import { useState } from "react";
import { useTheme } from "@/context/ThemeContext";
import { VISION_MODES, type VisionMode } from "@/lib/themes";

// ─────────────────────────────────────────────────────────────────────────────
// VisionAccessibilitySections — replaces the old single Color-blindness
// preview block with two independent segmented controls:
//   1. VISION MODE       — Standard / High Contrast (persisted, global)
//   2. COLOR VISION      — Standard / Red-blind / Green-blind / Blue-blind
//                          (preview-only, NOT persisted, applies a CSS filter
//                          to the local preview card)
// Visual style mirrors the Paper Texture and Text Size sections in
// ThemeStudio (same SubLabel + segmented row + Hint look).
// ─────────────────────────────────────────────────────────────────────────────

const VISION_LABELS: Record<VisionMode, string> = {
  standard: "Standard",
  high_contrast: "High Contrast",
};
const VISION_HINTS: Record<VisionMode, string> = {
  standard: "Default theme colors as designed.",
  high_contrast: "Maximum readability — pure black, white, and grey only.",
};

type ColorVision = "standard" | "red_blind" | "green_blind" | "blue_blind";
const COLOR_VISIONS: ColorVision[] = ["standard", "red_blind", "green_blind", "blue_blind"];
const COLOR_VISION_LABELS: Record<ColorVision, string> = {
  standard: "Standard",
  red_blind: "Red-blind",
  green_blind: "Green-blind",
  blue_blind: "Blue-blind",
};
const COLOR_VISION_FILTER: Record<ColorVision, string | undefined> = {
  standard: undefined,
  red_blind: "url(#protanopia-filter)",
  green_blind: "url(#deuteranopia-filter)",
  blue_blind: "url(#tritanopia-filter)",
};

export function VisionAccessibilitySections() {
  const { prefs, setPrefs } = useTheme();
  // Color-vision preview is a personal reference tool — session-only state.
  const [colorVision, setColorVision] = useState<ColorVision>("standard");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      {/* Hidden SVG filter defs — referenced by the preview card. */}
      <svg style={{ display: "none" }} aria-hidden="true">
        <defs>
          <filter id="protanopia-filter" colorInterpolationFilters="sRGB">
            <feColorMatrix type="matrix" values="
              0.567 0.433 0     0 0
              0.558 0.442 0     0 0
              0     0.242 0.758 0 0
              0     0     0     1 0
            " />
          </filter>
          <filter id="deuteranopia-filter" colorInterpolationFilters="sRGB">
            <feColorMatrix type="matrix" values="
              0.625 0.375 0     0 0
              0.7   0.3   0     0 0
              0     0.3   0.7   0 0
              0     0     0     1 0
            " />
          </filter>
          <filter id="tritanopia-filter" colorInterpolationFilters="sRGB">
            <feColorMatrix type="matrix" values="
              0.95  0.05  0     0 0
              0     0.433 0.567 0 0
              0     0.475 0.525 0 0
              0     0     0     1 0
            " />
          </filter>
        </defs>
      </svg>

      {/* ── Vision Mode ──────────────────────────────────────────────── */}
      <div role="radiogroup" aria-labelledby="theme-studio-vision-label">
        <SubLabel id="theme-studio-vision-label">Vision mode</SubLabel>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {VISION_MODES.map((id, i) => {
            const active = prefs.visionMode === id;
            return (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={active}
                aria-label={`Vision mode: ${VISION_LABELS[id]}`}
                onClick={() => {
                  if (prefs.visionMode !== id) void setPrefs({ visionMode: id });
                }}
                style={{
                  flex: 1,
                  padding: "10px 12px",
                  background: active ? "hsl(var(--ink))" : "hsl(var(--cream))",
                  color: active ? "hsl(var(--ink-fg))" : "hsl(var(--ink))",
                  border: "1px solid hsl(var(--ink))", borderRadius: 999,
                  cursor: "pointer",
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 10,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                }}
              >
                {VISION_LABELS[id]}
              </button>
            );
          })}
        </div>
        <Hint>{VISION_HINTS[prefs.visionMode]}</Hint>
      </div>

      {/* ── Color Vision (preview-only) ──────────────────────────────── */}
      <div role="radiogroup" aria-labelledby="theme-studio-cvd-label">
        <SubLabel id="theme-studio-cvd-label">Color vision</SubLabel>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {COLOR_VISIONS.map((id, i) => {
            const active = colorVision === id;
            return (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={active}
                aria-label={`Color vision preview: ${COLOR_VISION_LABELS[id]}`}
                onClick={() => setColorVision(id)}
                style={{
                  flex: 1,
                  padding: "10px 8px",
                  background: active ? "hsl(var(--ink))" : "hsl(var(--cream))",
                  color: active ? "hsl(var(--ink-fg))" : "hsl(var(--ink))",
                  border: "1px solid hsl(var(--ink))", borderRadius: 999,
                  cursor: "pointer",
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 10,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                }}
              >
                {COLOR_VISION_LABELS[id]}
              </button>
            );
          })}
        </div>
        <Hint>Preview how your theme appears across different color vision types.</Hint>

        {/* Preview card — filter applied here only. */}
        <div style={{
          marginTop: 10,
          border: "1px solid hsl(var(--ink))",
          background: "hsl(var(--paper))",
          padding: 14,
          filter: COLOR_VISION_FILTER[colorVision],
        }}>
          <div style={{
            fontFamily: "'Fraunces', 'Playfair Display', serif",
            fontWeight: 700, fontSize: 16, color: "hsl(var(--ink))", lineHeight: 1.2,
          }}>
            Sample heading on paper
          </div>
          <div style={{
            marginTop: 4,
            fontFamily: "'Fraunces', 'Playfair Display', serif",
            fontSize: 12, color: "hsl(var(--ink-2))", lineHeight: 1.4,
          }}>
            Secondary body text — italicized aside.
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
            <Chip bg="hsl(var(--rust))" fg="hsl(var(--rust-fg))">Rust</Chip>
            <Chip bg="hsl(var(--marigold))" fg="hsl(var(--marigold-fg))">Marigold</Chip>
            <Chip bg="hsl(var(--sand))" fg="hsl(var(--sand-fg))">Sand</Chip>
            <Chip bg="hsl(var(--olive))" fg="hsl(var(--olive-fg))">Olive</Chip>
            <Chip bg="hsl(var(--sky))" fg="hsl(var(--sky-fg))">Sky</Chip>
            <Chip bg="hsl(var(--plum))" fg="hsl(var(--plum-fg))">Plum</Chip>
          </div>

          <div style={{
            marginTop: 10, padding: "8px 10px",
            background: "hsl(var(--ink))", color: "hsl(var(--ink-fg))",
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 11, letterSpacing: "0.06em",
          }}>
            Verified · 4.5:1 contrast
          </div>
        </div>
      </div>
    </div>
  );
}

function SubLabel({ children, id }: { children: React.ReactNode; id?: string }) {
  return (
    <div
      id={id}
      className="caps"
      style={{
        color: "hsl(var(--ink-2))",
        marginBottom: 8,
        display: "flex",
        alignItems: "center",
      }}
    >
      {children}
    </div>
  );
}

function Hint({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        marginTop: 6,
        fontFamily: "'Fraunces', 'Playfair Display', serif",
        fontSize: 11,
        color: "hsl(var(--ink-2))",
        lineHeight: 1.35,
      }}
    >
      {children}
    </div>
  );
}

function Chip({ bg, fg, children }: { bg: string; fg: string; children: React.ReactNode }) {
  return (
    <span style={{
      padding: "4px 10px",
      background: bg, color: fg,
      border: "1px solid hsl(var(--ink))",
      fontFamily: "'IBM Plex Mono', monospace",
      fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase",
    }}>
      {children}
    </span>
  );
}